import { inject, Injectable } from '@angular/core';
import { forkJoin, Observable, of, switchMap } from 'rxjs';
import {
  DubberEndUser,
  DubberEndUserRef,
  DubPoint,
  DubPointRef,
  DubPointsResult,
  MicrosoftTeamsComplianceRecordingPolicy,
} from '../../../shared/models/generated/ziroModels';
import { DubberFieldConfigResource } from '../../../shared/resources/dubber-field-config.resource';
import { MicrosoftTeamsComplianceRecordingPolicyRequest } from '../../microsoft/dubber-compliance-recording/dubber-compliance-recording-form/dubber-compliance-recording-form.component';
import { Nvp } from '../../../shared/models/nvp';
import { HttpClient } from '@angular/common/http';
import { AuditHeaderService } from '../../../shared/services/audit-header.service';
import { HttpOptions } from '../../../shared/resources/abstract/model.resource.abstract';

export interface DubPointAndResult {
  dubPointsResult: DubPointsResult;
  dubPoint: DubPoint;
}
@Injectable()
export class DubberService {
  private _dubberFieldConfigResource = inject(DubberFieldConfigResource);
  protected http = inject(HttpClient);
  private _auditHeaderService = inject(AuditHeaderService);

  // Dubber end user
  createDubberUser(
    tenantId: string | number,
    endUser: DubberEndUser,
    region: string,
    auditTags?: Nvp[],
  ): Observable<DubberEndUserRef> {
    const httpOptions = this._buildHttpOptions(auditTags);
    return this.http.post<DubberEndUserRef>(
      `/services/microsoft/macs/tenants/${tenantId}/dubber-regions/${region}/dubber-users`,
      endUser,
      httpOptions,
    );
  }

  getDubberEndUser(tenantId: string | number, dubberRegionId: string, dubberUserId: string): Observable<DubberEndUser> {
    return this.http.get<DubberEndUser>(
      `/services/microsoft/macs/tenants/${tenantId}/dubber-regions/${dubberRegionId}/dubber-users/${encodeURIComponent(dubberUserId)}`,
    );
  }

  updateDubberEndUser(
    tenantId: string | number,
    dubberRegionId: string,
    dubberUserId: string,
    body: DubberEndUser,
    auditTags: Nvp[],
  ): Observable<void> {
    const httpOptions = this._buildHttpOptions(auditTags);
    return this.http.put<void>(
      `/services/microsoft/macs/tenants/${tenantId}/dubber-regions/${dubberRegionId}/dubber-users/${encodeURIComponent(dubberUserId)}`,
      body,
      httpOptions,
    );
  }

  deleteDubberUser(
    tenantId: string,
    region: string,
    dubberUserId: string,
    upn: string,
    auditTags: Nvp[],
  ): Observable<[void, void]> {
    const deleteDubberEndUser$ = this._deleteDubberEndUser(tenantId, region, dubberUserId);
    return forkJoin([
      this.deleteDubPointIfExists(tenantId, region, dubberUserId, auditTags).pipe(
        switchMap(() => deleteDubberEndUser$),
      ),
      this._getAndUpdateCompliancePolicy(tenantId, upn),
    ]);
  }

  // Dub Points

  createDubPoint(
    tenantId: string | number,
    dubPoint: DubPoint,
    region: string,
    dubberUserId: string,
    auditTags?: Nvp[],
  ): Observable<DubPointRef> {
    const httpOptions = this._buildHttpOptions(auditTags);
    return this.http.post<DubPointRef>(
      `/services/microsoft/macs/tenants/${tenantId}/dubber-regions/${region}/dubber-users/${encodeURIComponent(dubberUserId)}/dub-points`,
      dubPoint,
      httpOptions,
    );
  }

  deleteDubPointIfExists(
    tenantId: string | number,
    region: string,
    dubberUserId: string,
    auditTags: Nvp[],
  ): Observable<void> {
    return this._getUserDubPointsAndThrowErrorIfMultiple(tenantId, region, dubberUserId).pipe(
      switchMap((dubPointsResult) => {
        const dubPointId = dubPointsResult.dubPointRefJsons.length ? dubPointsResult.dubPointRefJsons[0].id : null;
        return dubPointsResult.dubPointRefJsons.length
          ? this._deleteDubPoint(tenantId, region, dubberUserId, dubPointId, auditTags)
          : of(null);
      }),
    );
  }

  getDubPointsResultAndDubPointByRegionAndUserId(
    tenantId: string | number,
    dubberRegionId: string,
    dubberUserId: string,
  ): Observable<DubPointAndResult> {
    return this._getUserDubPointsAndThrowErrorIfMultiple(tenantId, dubberRegionId, dubberUserId).pipe(
      switchMap((dubPointsResult: DubPointsResult) => {
        return dubPointsResult.dubPointRefJsons.length
          ? this._getDubPoint(tenantId, dubberRegionId, dubberUserId, dubPointsResult.dubPointRefJsons[0].id).pipe(
              switchMap((dubPoint) => of({ dubPointsResult, dubPoint })),
            )
          : of(null);
      }),
    );
  }

  // Compliance Recording Policy

  getUserComplianceRecordingPolicy(tenantId: string, upn: string): Observable<MicrosoftTeamsComplianceRecordingPolicy> {
    return this.http.get<MicrosoftTeamsComplianceRecordingPolicy>(
      `/services/microsoft/macs/tenants/${tenantId}/teams-compliance-recording-policies/${encodeURIComponent(upn)}`,
    );
  }

  updateUserComplianceRecordingPolicy(
    tenantId: string,
    body: MicrosoftTeamsComplianceRecordingPolicyRequest,
    auditTags?: Nvp[],
  ): Observable<void> {
    const httpOptions = this._buildHttpOptions(auditTags);
    return this.http.put<void>(
      `/services/microsoft/macs/tenants/${tenantId}/teams-compliance-recording-policies/${encodeURIComponent(body.userPrincipalName)}`,
      body,
      httpOptions,
    );
  }

  private _getAndUpdateCompliancePolicy(tenantId: string, upn: string): Observable<void> {
    return this._dubberFieldConfigResource.getComplianceRecordingPolicy(tenantId, false).pipe(
      switchMap((complianceFieldConfig) => {
        const recordingPolicy: MicrosoftTeamsComplianceRecordingPolicyRequest = {
          complianceRecordingPolicy: complianceFieldConfig.complianceRecordingPolicy.defaultOption,
          userPrincipalName: upn,
        };
        return this.updateUserComplianceRecordingPolicy(tenantId, recordingPolicy);
      }),
    );
  }

  private _deleteDubberEndUser(
    tenantId: string | number,
    dubberRegionId: string,
    dubberUserId: string,
    auditTags?: Nvp[],
  ): Observable<void> {
    const httpOptions = this._buildHttpOptions(auditTags);
    return this.http.delete<void>(
      `/services/microsoft/macs/tenants/${tenantId}/dubber-regions/${dubberRegionId}/dubber-users/${encodeURIComponent(dubberUserId)}`,
      httpOptions,
    );
  }
  private _getDubPoint(
    tenantId: string | number,
    dubberRegionId: string,
    dubberUserId: string,
    dubPointId: string,
  ): Observable<DubPoint> {
    return this.http.get<DubPoint>(
      `/services/microsoft/macs/tenants/${tenantId}/dubber-regions/${dubberRegionId}/dubber-users/${encodeURIComponent(
        dubberUserId,
      )}/dub-points/${dubPointId}`,
    );
  }

  private _getUserDubPoints(
    tenantId: string | number,
    dubberRegionId: string,
    dubberUserId: string,
  ): Observable<DubPointsResult> {
    return this.http.get<DubPointsResult>(
      `/services/microsoft/macs/tenants/${tenantId}/dubber-regions/${dubberRegionId}/dubber-users/${encodeURIComponent(
        dubberUserId,
      )}/dub-points`,
    );
  }

  private _getUserDubPointsAndThrowErrorIfMultiple(
    tenantId: string | number,
    region: string,
    dubberUserId: string,
  ): Observable<DubPointsResult> {
    return new Observable((subscriber) => {
      this._getUserDubPoints(tenantId, region, dubberUserId).subscribe((dubPointsResult) => {
        if (dubPointsResult.dubPointRefJsons.length > 1) {
          throw new Error('Users with multiple dubpoints configured are not supported');
        } else {
          subscriber.next(dubPointsResult);
          subscriber.complete();
        }
      });
    });
  }

  private _deleteDubPoint(
    tenantId: string | number,
    dubberRegionId: string,
    dubberUserId: string,
    dubPointId: string,
    auditTags?: Nvp[],
  ): Observable<void> {
    const httpOptions = this._buildHttpOptions(auditTags);
    return this.http.delete<void>(
      `/services/microsoft/macs/tenants/${tenantId}/dubber-regions/${dubberRegionId}/dubber-users/${encodeURIComponent(
        dubberUserId,
      )}/dub-points/${dubPointId}`,
      httpOptions,
    );
  }

  private _buildHttpOptions(auditTags: Nvp[]): HttpOptions | object {
    return auditTags ? { headers: this._auditHeaderService.buildHeader(auditTags) } : {};
  }
}
